import '../../styles/components/dropdowns/profileDropdown.scss';
import userPic from '../../imgs/user.jpg';
import CustomSVGs from '../../ui/customSVGs/CustomSVGs';
import {
  logoutSVG,
  settingsPlusSVG,
  creditCardSVG,
  questionSVG,
  switchShopSVG,
} from '../../lib/allCustomSVGs';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import MainSquareButton from '../../ui/buttons/MainSquareButton';
import { RootState } from '../../store/store';
import { useTranslation } from 'react-i18next';
import { signOutOfApp } from '../../lib/api/sign-out';
import {
  setIsPaymentPopupOpen,
  setIsSwitchShopPopupOpen,
} from '../../store/reducers/uiSlice';
import truncate from '../../lib/helper-functions/truncate';
import isNotEmptyString from '../../lib/helper-functions/isNotEmptyString';
import { useCan } from '../../lib/rbac';
import { usePlanLock } from '../../lib/plan-lock/usePlanLock';

const ProfileDropdown = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userProfile = useSelector((state: RootState) => state.user.profile);
  const employeeIdentity = useSelector(
    (state: RootState) => state.auth?.agency?.employeeIdentity
  );
  const taraTrialDetails = useSelector(
    (state: RootState) => state.user.shop.taraTrialDetails
  ) || { is_trial: false, trial_available_days: undefined };
  const appTheme = useSelector((state: RootState) => state.UI.theme.dark);
  const taraSubscriptionDetails = useSelector(
    (state: RootState) => state.user?.shop?.taraSubscriptionDetails
  );

  const canViewBilling = useCan('billing.view');
  // No active plan or trial: Settings (a product area) leaves the menu.
  const { locked: isPlanLocked } = usePlanLock();

  const activeShopName = userProfile?.all_shops?.find(
    (shop: any) => shop?.slug === userProfile?.active_shop_slug
  )?.name;

  const getProfileName = () => {
    if (userProfile?.first_name || userProfile?.last_name) {
      return `${userProfile?.first_name} ${userProfile?.last_name}`;
    } else if (userProfile?.email) {
      return `${userProfile.email}`;
    } else if (employeeIdentity?.name) {
      // 2026-09-07 — invite-only employees have no seller profile; use /me.
      return employeeIdentity.name;
    } else if (employeeIdentity?.email) {
      return employeeIdentity.email;
    } else {
      return t('User');
    }
  };

  const logoutHandler = () => {
    const next = signOutOfApp();
    if (next) {
      navigate(next);
    }
  };

  return (
    <div className='profileDropdown'>
      <div className='profileDropdown__top'>
        <div className='profileDropdown__top--pic'>
          <img src={userPic} alt='user-pic' />
        </div>
        <div className='profileDropdown__top--text'>
          <p>{truncate(getProfileName(), 15)}</p>
        </div>
      </div>
      {/* --------------- Trial End Banner --------------- */}
      {taraTrialDetails &&
        taraTrialDetails?.is_trial &&
        taraTrialDetails?.trial_available_days &&
        taraTrialDetails?.trial_available_days > 0 && (
          <div
            className='sidebarTara__banner'
            style={{
              marginTop: '-0.7rem',
              marginBottom: '0.3rem',
            }}
            onClick={() => {
              dispatch(setIsPaymentPopupOpen(true));
            }}
          >
            <div className='sidebarTara__banner--left'>
              <h3>
                {t('TrialEndsInDays', {
                  days: taraTrialDetails?.trial_available_days,
                })}
              </h3>
            </div>
          </div>
        )}
      {/* --------------- Pricing Plans --------------- */}
      {/* Opens the plans page on its own path, for roles that may see billing. */}
      {canViewBilling &&
        (!taraSubscriptionDetails ||
          taraSubscriptionDetails?.currentSubscriptionStatus === 'none') && (
          <div className='profileDropdown__center'>
            <div
              onClick={() => {
                navigate('/settings/subscription');
              }}
              className='profileDropdown__center--item'
            >
              <div
                data-profile-settings-mode={appTheme?.toString()}
                className='profileDropdown__center--item-left profileDropdown__center--item-mode'
              >
                {<CustomSVGs svg={creditCardSVG} />}
              </div>
              <div className='profileDropdown__center--item-right'>
                {<p>{t('PricingPlans')}</p>}
              </div>
            </div>
          </div>
        )}
      {/* --------------- Subscription --------------- */}
      {taraSubscriptionDetails &&
        taraSubscriptionDetails?.currentSubscriptionStatus === 'active' && (
          <div className='profileDropdown__center'>
            <div
              onClick={() => {
                navigate('/settings/subscription');
              }}
              className='profileDropdown__center--item'
            >
              <div
                data-profile-settings-mode={appTheme?.toString()}
                className='profileDropdown__center--item-left profileDropdown__center--item-mode'
              >
                {<CustomSVGs svg={creditCardSVG} />}
              </div>
              <div className='profileDropdown__center--item-right'>
                {<p>{t('Subscription')}</p>}
              </div>
            </div>
          </div>
        )}
      {/* --------------- Settings --------------- */}
      {!isPlanLocked && (
        <div className='profileDropdown__center'>
          <div
            onClick={() => {
              navigate('/settings');
            }}
            className='profileDropdown__center--item'
          >
            <div
              data-profile-settings-mode={appTheme?.toString()}
              className='profileDropdown__center--item-left profileDropdown__center--item-mode'
            >
              {<CustomSVGs svg={settingsPlusSVG} />}
            </div>
            <div className='profileDropdown__center--item-right'>
              {<p>{t('Settings')}</p>}
            </div>
          </div>
        </div>
      )}
      {/* --------------- Theme --------------- */}
      {/* <div className='profileDropdown__center'>
        <div
          onClick={() => {
            dispatch(setAppThemeMode());
          }}
          className='profileDropdown__center--item'
        >
          <div
            data-profile-mode={appTheme?.toString()}
            className='profileDropdown__center--item-left profileDropdown__center--item-mode'
          >
            {appTheme ? (
              <CustomSVGs svg={sunSVG} />
            ) : (
              <CustomSVGs svg={moonSVG} />
            )}
          </div>
          <div className='profileDropdown__center--item-right'>
            {!appTheme && <p>{t('DarkMode')}</p>}
            {appTheme && <p>{t('LightMode')}</p>}
          </div>
        </div>
      </div> */}
      {/* --------------- Support --------------- */}
      <div className='profileDropdown__center'>
        <div
          onClick={() => {
            window.open('https://intercom.help/ad-up/en', '_blank');
          }}
          className='profileDropdown__center--item'
        >
          <div
            data-profile-settings-mode={appTheme?.toString()}
            className='profileDropdown__center--item-left profileDropdown__center--item-mode'
          >
            {<CustomSVGs svg={questionSVG} />}
          </div>
          <div className='profileDropdown__center--item-right'>
            {<p>{t('Support')}</p>}
          </div>
        </div>
      </div>
      {/* --------------- Switch Shop --------------- */}
      {userProfile &&
        userProfile?.role === 'agency' &&
        userProfile?.all_shops &&
        Array.isArray(userProfile?.all_shops) &&
        userProfile?.all_shops?.length > 0 && (
          <div className='profileDropdown__center'>
            <div
              onClick={() => {
                dispatch(setIsSwitchShopPopupOpen(true));
              }}
              className='profileDropdown__center--item'
            >
              <div
                data-profile-settings-mode={appTheme?.toString()}
                className='profileDropdown__center--item-left profileDropdown__center--item-mode'
              >
                {<CustomSVGs svg={switchShopSVG} />}
              </div>
              <div className='profileDropdown__center--item-right'>
                {
                  <p>
                    {activeShopName && isNotEmptyString(activeShopName)
                      ? truncate(activeShopName, 15)
                      : t('SwitchShop')}
                  </p>
                }
              </div>
            </div>
          </div>
        )}
      {/* --------------- Logout --------------- */}
      <div className='profileDropdown__bottom'>
        <MainSquareButton
          value={t('Logout')}
          icon={<CustomSVGs svg={logoutSVG} />}
          disabled={false}
          onClick={(e) => {
            e.preventDefault();
            logoutHandler();
          }}
        />
      </div>
    </div>
  );
};

export default ProfileDropdown;
